'use client';

import { RoomEvent } from 'livekit-client';
import * as React from 'react';
import { useLocalParticipant, useParticipants, useRoomContext } from '@livekit/components-react';
import {
  HAND_RAISED_AT_KEY,
  parseRaisedAt,
  raisedHandRank,
  rankRaisedHands,
  type RaisedHandEntry,
} from './raise-hand';

export const useRaisedHandQueue = (): RaisedHandEntry[] => {
  const room = useRoomContext();
  const participants = useParticipants();
  const [revision, setRevision] = React.useState(0);

  React.useEffect(() => {
    const bump = () => setRevision((current) => current + 1);
    room.on(RoomEvent.ParticipantAttributesChanged, bump);
    return () => {
      room.off(RoomEvent.ParticipantAttributesChanged, bump);
    };
  }, [room]);

  return React.useMemo(
    () => rankRaisedHands(participants),
    // revision re-reads in-place attribute mutations on the same participant objects
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [participants, revision],
  );
};

export const useRaiseHand = (): {
  isRaised: boolean;
  queue: RaisedHandEntry[];
  rank: number | null;
  toggleRaiseHand: () => Promise<void>;
} => {
  const { localParticipant } = useLocalParticipant();
  const queue = useRaisedHandQueue();
  const isRaised = parseRaisedAt(localParticipant.attributes[HAND_RAISED_AT_KEY]) !== null;
  const rank = raisedHandRank(queue, localParticipant.identity);

  const toggleRaiseHand = React.useCallback(async () => {
    const nextValue = isRaised ? '' : String(Date.now());
    await localParticipant.setAttributes({ [HAND_RAISED_AT_KEY]: nextValue });
  }, [isRaised, localParticipant]);

  return { isRaised, queue, rank, toggleRaiseHand };
};
